import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ClockIcon } from 'lucide-react';
import { useProto } from '../../contexts/PrototypeContext';
import { formatCountdown, formatMoney } from '../../utils/format';
import { Annotation } from '../shared/Annotation';
const ease = [0.23, 1, 0.32, 1] as const;
export function CartSummary({
  device
}: {
  device: 'mobile' | 'desktop';
}) {
  const {
    cart,
    discountApplied,
    holdRemainingMs,
    actions
  } = useProto();
  return <aside aria-labelledby="summary-title" className={`rounded-lg border border-i9-line bg-i9-surface p-5 font-i9 ${device === 'desktop' ? 'sticky top-6' : ''}`} data-id="element-224" data-mp="19vejs119zsxp">
      <h2 id="summary-title" className="text-[17px] font-bold text-i9-ink" data-id="element-225" data-mp="1esxyt35ss9qp">Order summary</h2>
      <dl className="mt-4 space-y-2.5 text-[14px]" data-id="element-226" data-mp="1jntz0ql6u3ew">
        <div className="flex justify-between gap-3" data-id="element-227" data-mp="m750b5rjbh9q">
          <dt className="text-i9-muted" data-id="element-228" data-mp="m4itx9gdr7t3">Subtotal ({cart.items.length} registrations)</dt>
          <dd className="tabular-nums text-i9-ink" data-id="element-229" data-mp="r3dby96iwsc5">{formatMoney(cart.subtotal)}</dd>
        </div>
        {cart.adjustments.map(a => <div key={a.id} className="flex justify-between gap-3" data-id="element-230" data-mp="1tw2bnmseqsn7">
            <dt className="text-i9-muted" data-id="element-231" data-mp="1tyndwcwmbalh">
              {a.label} <span className="text-[12px]" data-id="element-232" data-mp="1p4sp4v25o5fj">· {a.detail}</span>
            </dt>
            <dd className="tabular-nums text-i9-ink" data-id="element-233" data-mp="1l4sbvdqsm0a2">{formatMoney(a.amount)}</dd>
          </div>)}
        <AnimatePresence initial={false} data-id="element-234" data-mp="1g5fp0szx8t7h">
          {cart.volunteer && <motion.div key="vrd" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.22,
          ease
        }} className="overflow-hidden" data-id="element-235" data-mp="1b62tljuzidta">
              <div className="flex justify-between gap-3" data-id="element-236" data-mp="30kr5ljznxtqs">
                <dt className="flex items-center gap-1.5 text-i9-muted" data-id="element-237" data-mp="30k7t4iss7xk5">
                  {cart.volunteer.label}
                  <CheckIcon className="h-3.5 w-3.5 shrink-0 text-i9-greenIcon" strokeWidth={3} aria-label="Applied" data-id="element-238" data-mp="2whl0ifoae8lw" />
                </dt>
                <dd className="tabular-nums text-i9-ink" data-id="element-239" data-mp="ynqlenx3d54p">{formatMoney(cart.volunteer.amount)}</dd>
              </div>
            </motion.div>}
        </AnimatePresence>
      </dl>

      <div className="mt-4 flex items-baseline justify-between border-t border-i9-line pt-4" data-id="element-240" data-mp="ox5snh43mutp">
        <span className="font-bold text-i9-ink" data-id="element-241" data-mp="1cmqb7ywcbq2g">Total due today</span>
        <motion.span key={cart.total} initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="text-[24px] font-extrabold tabular-nums text-i9-ink" aria-live="polite" data-id="element-242" data-mp="1clf7fz2waibc">
          {formatMoney(cart.total)}
        </motion.span>
      </div>

      {discountApplied && <div className="mt-4 space-y-2" data-id="element-243" data-mp="3oevlou3vwzgb">
          <p className="flex items-start gap-2 rounded-md border border-i9-line bg-white px-3 py-2.5 text-[12.5px] text-i9-muted" data-id="element-244" data-mp="3tcf1hyg8dkad">
            <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-245" data-mp="3ktk5ees9s6zx" />
            <span data-id="element-246" data-mp="3gs88rpazali8">
              Discount reserved for <span className="font-bold tabular-nums text-i9-ink" data-id="element-247" data-mp="3gvclxc6f8rl1">{formatCountdown(holdRemainingMs)}</span>. Complete checkout to keep it.
            </span>
          </p>
          <Annotation kind="placeholder" title="Decision #1 · 30-minute hold is not approved" data-id="element-248" data-mp="3pv9dpmo3y6kb">
            Reservation duration isn't finalized, and whether customers should see a countdown is an open question.
          </Annotation>
          <Annotation kind="unresolved" title="Decision #3 · reservation & cart persistence" data-id="element-249" data-mp="2x37oief1qzx7">
            Undefined: what happens to this hold if the customer closes the browser, signs out, returns to an existing cart, or switches devices before it expires.
          </Annotation>
        </div>}

      <button type="button" onClick={actions.checkout} className="mt-5 w-full bg-i9-steel py-3.5 text-[16px] font-bold text-white shadow-md transition-colors duration-150 hover:bg-i9-steelDark" data-id="element-250" data-mp="2jbj9a29i3vc0">
        Next: Payment
      </button>
      <button type="button" onClick={() => actions.goToStep('volunteer')} className="mt-2 w-full py-2.5 text-[14px] font-semibold text-i9-steel transition-colors hover:text-i9-steelDark" data-id="element-251" data-mp="1mnafiibveqv8">
        Back to Volunteer Info
      </button>
    </aside>;
}